/**
 * WCO-2322 (6B) — ProductionOutputScanModal
 *
 * BRAND-NEW per-OUTPUT scan modal (the old ShipOutScannerModal is left untouched for reference).
 * Order (per César): Step 1 Scan Badge → Step 2 Select Output/Transfer → Step 3 Scan Cases.
 *
 * Step 2 lists BOTH transfer-back types for the day:
 *   - Output rows   (production outputs, one per destination) → scan via productionOutputScanService
 *   - Transfer rows (on-demand +Transfer warehouse transfers) → scan via the existing hteTransferService
 * Step 3 routes by the selected row's type. "Switch" returns to Step 2 (badge persists). All integrity rules
 * (single-output uniqueness, session↔output binding, cross-type rejection) are enforced by the backend.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { productionOutputScanService } from '../../../services/productionOutputScanService';
import { dailyTransferOutService } from '../../../services/dailyTransferOutService';
import { wsService } from '../../../services/websocket';
import { authService } from '../../../services/api';
import { inventoryService } from '../../../services/inventoryService';
import type { OutputReconciliation, OutputSessionBreakdown, RawMaterialTransferRow, ConfirmContainer } from '../../../services/dailyTransferOutService';
import { isBadgeCode, extractEmployeeNumber, parseGS1128Barcode } from '../../../utils/barcodeUtils';
import { scanErrorMessage } from '../../../utils/scanErrorMessages';
import { FP_NI_TAG_CLASSES } from '../../../pages/Inventory/inventoryHelpers';
import HoverTooltip from './HoverTooltip';
import { useBarcodeScanner } from '../../../hooks/useBarcodeScanner';
import toast from 'react-hot-toast';
import { X, User, Package, Truck, ArrowLeft, Trash2, Check, CheckCircle2, ListChecks, ChevronDown, ChevronRight, Plus } from 'lucide-react';

interface ProductionOutputScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;                             // production date (for the day-wide Confirm summary)
  outputs: OutputReconciliation[];          // dto.outputs — Output rows (active filtered here)
  transfers: RawMaterialTransferRow[];      // dto.raw_material_transfers — Transfer rows
  unscannedCount?: number;                  // dto.unscanned_count — finished boxes produced but not scanned to any output
  onChanged?: () => void;                   // refresh the parent summary after each scan/undo
}

type Step = 'badge' | 'select' | 'scan' | 'confirm';
type RowType = 'output' | 'transfer';
interface SelectedRow { type: RowType; number: string; destination: string; expected: number }
interface ScanRow { id: string; label: string; weight: number | null; source?: string | null; barcode?: string | null; name?: string | null; sessionNumber?: string | null }

// WCO-2412: collapse a container's scanned boxes into one row per item (item_number + source, so a
// FP-NI case of an item stays distinct from a finished case of the same item). qty = case count;
// total_weight = SUM of each case's own weight (cases are catch-weight — never qty × unit).
// WCO-2812: item_number CAN be null at runtime (backend COALESCEs to NULL for an unmapped
// by-product/buffer scan). Type it string | null so the compiler catches future unguarded string
// uses that would reintroduce the Confirm-step crash, instead of the type silently lying.
interface AggregatedItem { key: string; item_number: string | null; item_english_name?: string | null; source?: string | null; qty: number; total_weight: number; boxes: ConfirmContainer['items'] }
export function aggregateConfirmItems(items: ConfirmContainer['items']): AggregatedItem[] {
  const byKey = new Map<string, AggregatedItem>();
  for (const it of items) {
    const key = `${it.item_number}|${it.source ?? 'finished'}`;
    const existing = byKey.get(key);
    const w = it.actual_weight != null ? Number(it.actual_weight) || 0 : 0;
    if (existing) {
      existing.qty += 1;
      existing.total_weight += w;
      existing.boxes.push(it);
    } else {
      byKey.set(key, { key, item_number: it.item_number, item_english_name: it.item_english_name, source: it.source, qty: 1, total_weight: w, boxes: [it] });
    }
  }
  // Stable order: finished first, then by item number. Null-guard item_number — a scan can resolve
  // with no item_number (e.g. an unmapped by-product/buffer scan), and a raw .localeCompare on null
  // crashes the whole Confirm step. Coerce to '' so such a row sorts first-ish instead of throwing.
  return Array.from(byKey.values()).sort((a, b) =>
    (a.source === 'finished' || !a.source ? 0 : 1) - (b.source === 'finished' || !b.source ? 0 : 1)
    || (a.item_number ?? '').localeCompare(b.item_number ?? ''));
}

const ProductionOutputScanModal: React.FC<ProductionOutputScanModalProps> = ({
  isOpen,
  onClose,
  date,
  outputs,
  transfers,
  unscannedCount = 0,
  onChanged,
}) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<Step>('badge');
  // WCO-2680: true only when the CURRENT Confirm visit was reached by the auto-advance (nothing left to
  // scan), not by the user manually clicking "Review & Confirm". Distinguishes the two so a live refresh
  // that surfaces a fresh un-scanned output bounces the supervisor BACK to Select (they can't scan from
  // Confirm) — but only when they were auto-parked here, never interrupting a deliberate review-before-send.
  const [autoAdvancedToConfirm, setAutoAdvancedToConfirm] = useState(false);
  // WCO-2322 (Confirm): the day-wide review summary (fetched on entering the Confirm step).
  const [confirmData, setConfirmData] = useState<{ containers: ConfirmContainer[]; all_complete: boolean } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // Monotonic token for confirm-summary fetches. Every getConfirmSummary that writes confirmData bumps and
  // captures this; a response only wins if it's still the latest — so a slow older fetch can't clobber a
  // newer one (out-of-order writes on rapid sends). Mirrors the WCO-2599 manualCasesReqRef pattern.
  const confirmReqRef = useRef(0);
  // Only a registered SUPERVISOR may scan (validated via authService.verifySupervisorBadge).
  const [employee, setEmployee] = useState<{ employee_number: string; display_name: string } | null>(null);
  const [badgeEntry, setBadgeEntry] = useState('');
  const [badgeLoading, setBadgeLoading] = useState(false);
  const badgeInputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<SelectedRow | null>(null);
  const [scans, setScans] = useState<ScanRow[]>([]);
  // WCO-2413: scan-group session_numbers the user has EXPANDED. Collapsed by default — the header's
  // scanned/target counter is the reconciliation aid, so the box list stays hidden until tapped.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (session: string) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    next.has(session) ? next.delete(session) : next.add(session);
    return next;
  });
  // WCO-2412: Confirm-step aggregated item rows the user has EXPANDED (key = container#|itemKey).
  // Collapsed by default — the row shows qty + summed weight; expanding lists the individual boxes.
  const [expandedConfirmItems, setExpandedConfirmItems] = useState<Set<string>>(new Set());
  const toggleConfirmItem = (key: string) => setExpandedConfirmItems((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const [scannedCount, setScannedCount] = useState(0); // counts toward expected (Output: FINISHED only)
  const [extraCount, setExtraCount] = useState(0);     // Output: FP-NI / by-products scanned (extras, not gating)
  const [sending, setSending] = useState(false);
  // WCO-2433: pending send confirmation — a single container number, or 'ALL' for "send all ready".
  const [sendTarget, setSendTarget] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: RowType; number: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Resolve location code (e.g. "X") → name (e.g. "Glendale Sales") for the Select Output list.
  const [locationNames, setLocationNames] = useState<Record<string, string>>({});
  useEffect(() => {
    inventoryService.getLocationCodes()
      .then((locs) => setLocationNames(Object.fromEntries(locs.map((l) => [l.location_code, l.description]))))
      .catch(() => { /* fall back to the raw code */ });
  }, []);
  const locName = (code: string) => locationNames[code] || code;

  // WCO-2433: exclude cancelled, but KEEP already-sent outputs (ship_status !== 'draft') visible so the
  // supervisor can see a destination is already gone — they render disabled with a "Sent" badge below.
  const activeOutputs = outputs.filter((o) => o.status === 'active');
  const isSentOutput = (o: OutputReconciliation) => (o.ship_status ?? 'draft') !== 'draft';
  const isSentTransfer = (tr: RawMaterialTransferRow) => (tr.status ?? 'draft') !== 'draft';

  // WCO-2680: nothing left to scan — every active output AND every transfer is already sent (or a
  // feeder was cancelled leaving a done/empty day). There is nothing to pick on the Select step, so
  // after the badge we skip straight to the Confirm summary (which shows each row as Sent/complete)
  // instead of parking the supervisor on a list of un-clickable "Sent" rows. Requires ≥1 row to exist
  // (an empty modal keeps the normal Select step so the "no outputs" empty-state still renders).
  //   Edge case (unscannedCount): a day can have every output sent yet still have finished boxes that
  //   were never scanned to any destination (produced beyond a reduced target, or for a cancelled
  //   output). The day-level unscanned-box guard is skipped once all outputs are sent, so we must check
  //   it here — otherwise the "all sent" banner would falsely reassure while orphan boxes remain. Only
  //   auto-advance when there is genuinely nothing left: all rows sent AND no unscanned boxes.
  const hasAnyRow = activeOutputs.length > 0 || transfers.length > 0;
  const nothingLeftToScan =
    hasAnyRow && activeOutputs.every(isSentOutput) && transfers.every(isSentTransfer) && unscannedCount === 0;

  // The Confirm-step "all sent" state derived from the just-refetched review (confirmData), so it renders
  // IMMEDIATELY after "Send all ready" without waiting for the parent's outputs/transfers props to catch
  // up (which drive nothingLeftToScan). Both are OR'd for the completed-state UI so the modal never shows a
  // dead "Send all ready" between the send and the prop refresh.
  //   Must ALSO require unscannedCount === 0 — same guard as nothingLeftToScan. Otherwise, when every
  //   container is sent but finished boxes remain unscanned to any destination, the "all sent" banner would
  //   falsely reassure while orphan boxes still exist (the exact case nothingLeftToScan guards against).
  const allContainersSent =
    !!confirmData && confirmData.containers.length > 0 && confirmData.containers.every((c) => c.sent)
    && unscannedCount === 0;
  const showAllSentState = nothingLeftToScan || allContainersSent;

  // WCO-2680: at least one container is COMPLETE and UNSENT — i.e. eligible to ship. Used to offer a
  // "Next → Review" button on the Select step so a supervisor whose containers are already fully scanned
  // can jump straight to Confirm without re-opening an output first. Output: unsent + scanned >= target.
  // Transfer: unsent + has ≥1 scan (scan-only model).
  const anySendable =
    activeOutputs.some((o) => !isSentOutput(o) && o.actual_cases >= (o.suggested_target ?? o.expected_cases)) ||
    transfers.some((tr) => !isSentTransfer(tr) && tr.scanned_count > 0);

  // WCO-2322: the selected row's planned total is reached → the Review and Confirm button turns blue
  // (purely a color aid; the button stays clickable either way).
  const allScanned = !!selected && selected.expected > 0 && scannedCount >= selected.expected;

  // WCO-2412: the full reconciliation (incl. session_breakdown) for the selected OUTPUT row, looked up
  // from the outputs prop by number. Only outputs carry a breakdown (transfers don't).
  const selectedOutput = selected?.type === 'output'
    ? activeOutputs.find((o) => o.output_number === selected.number)
    : undefined;

  // WCO-2413: group the flat scan list by producing session→product for a self-reconcilable view.
  // Each group = one feeding session's {planned, target, delta} from session_breakdown, with the live
  // finished scans bucketed by session_number. Non-finished scans (FP-NI / by-product) have no session
  // and fall into a trailing "extras" group. Groups sum to the output's suggested_target.
  const scanGroups = useMemo(() => {
    const breakdown: OutputSessionBreakdown[] = selectedOutput?.session_breakdown ?? [];
    const bySession = new Map<string, ScanRow[]>();
    const extras: ScanRow[] = [];
    for (const s of scans) {
      if (s.source && s.source !== 'finished') { extras.push(s); continue; }
      const key = s.sessionNumber || '';
      if (!key) { extras.push(s); continue; }
      (bySession.get(key) ?? bySession.set(key, []).get(key)!).push(s);
    }
    const groups = breakdown.map((b) => ({
      session_number: b.session_number,
      item_number: b.item_number,
      item_english_name: b.item_english_name,
      planned: b.planned,
      target: b.target,
      delta: b.delta,
      scans: bySession.get(b.session_number) ?? [],
    }));
    // Any scanned session not present in the plan (defensive — shouldn't happen) becomes its own group.
    for (const [session, rows] of bySession) {
      if (!breakdown.some((b) => b.session_number === session)) {
        groups.push({ session_number: session, item_number: rows[0]?.label ?? null, item_english_name: rows[0]?.name ?? null, planned: 0, target: 0, delta: 0, scans: rows });
      }
    }
    return { groups, extras };
  }, [selectedOutput, scans]);

  // WCO-2545: transfer scans grouped by item (collapsible, like the output view) — keeps the list compact
  // so the "Add damaged case" affordance stays prominent. Group key = item number; header shows the count.
  const transferScanGroups = useMemo(() => {
    const byItem = new Map<string, { item_number: string; item_english_name?: string | null; scans: ScanRow[] }>();
    for (const s of scans) {
      const key = s.label || '(unknown)';
      const g = byItem.get(key) ?? byItem.set(key, { item_number: key, item_english_name: s.name, scans: [] }).get(key)!;
      g.scans.push(s);
    }
    return Array.from(byItem.values());
  }, [scans]);

  // WCO-2680: reactively auto-advance off the Select step when a live refresh (WS: session added/
  // cancelled → parent refetch → new outputs/transfers props) leaves nothing left to scan. The badge-time
  // check only fires once; without this, removing the last scannable output while the supervisor sits on
  // Select would strand them on a list of un-clickable "Sent" rows until a manual refresh. Only fires from
  // 'select' (never mid-scan or already on confirm), and only after the badge (employee set).
  useEffect(() => {
    if (isOpen && employee && step === 'select' && nothingLeftToScan) {
      setAutoAdvancedToConfirm(true);
      setStep('confirm');
    }
  }, [isOpen, employee, step, nothingLeftToScan]);

  // WCO-2680: the mirror of the above. If the supervisor was AUTO-advanced onto Confirm (nothing to scan)
  // and a live refresh then surfaces a fresh un-scanned output/transfer (manager added a session), pull
  // them BACK to Select — they can't scan from Confirm, so the scannable row is where they need to be.
  // Gated on autoAdvancedToConfirm so a DELIBERATE "Review & Confirm" (manual) is never interrupted by a
  // poll or a known-incomplete row; clearing the flag makes this a one-shot bounce, not a ping-pong.
  useEffect(() => {
    if (isOpen && employee && step === 'confirm' && autoAdvancedToConfirm && !nothingLeftToScan) {
      setAutoAdvancedToConfirm(false);
      setStep('select');
    }
  }, [isOpen, employee, step, autoAdvancedToConfirm, nothingLeftToScan]);

  // WCO-2680: a signal that changes whenever the day's containers change (added/removed/sent) — used to
  // re-fetch the Confirm review when a live refresh lands WHILE the supervisor is already on Confirm.
  // Without it, a manager adding a session (new scannable output) leaves the Confirm body showing the stale
  // summary even though the footer already re-enables Back/Send. Keyed on each row's number + sent state.
  const containersSignal = useMemo(
    () => [
      ...activeOutputs.map((o) => `o:${o.output_number}:${o.ship_status ?? 'draft'}`),
      ...transfers.map((tr) => `t:${tr.transfer_number}:${tr.status ?? 'draft'}`),
    ].sort().join('|'),
    [activeOutputs, transfers]
  );

  // Load the day-wide review whenever we enter the Confirm step — or when the day's containers change
  // (containersSignal) while already on Confirm, so a live add/cancel stays reflected in the review.
  useEffect(() => {
    if (step !== 'confirm' || !isOpen) return;
    setConfirmLoading(true);
    setExpandedConfirmItems(new Set()); // WCO-2412: aggregated rows start collapsed each visit
    const reqId = ++confirmReqRef.current; // guard: a stale fetch resolving late must not overwrite newer state
    dailyTransferOutService.getConfirmSummary(date)
      .then((next) => { if (reqId === confirmReqRef.current) setConfirmData(next); })
      .catch(() => { if (reqId === confirmReqRef.current) setConfirmData({ containers: [], all_complete: false }); })
      .finally(() => { if (reqId === confirmReqRef.current) setConfirmLoading(false); });
  }, [step, isOpen, date, containersSignal]);

  // WCO-2728: react to session changes made ELSEWHERE while this modal is open (e.g. a manager cancels
  // or adds a feeding session in Production Guide). The parent (DailyTransferOutView) subscribes too, but
  // the modal reads its own data — the Select-step Actual Breakdown from the `outputs` prop and the
  // Confirm-step review from its own getConfirmSummary fetch — so without this it keeps a stale
  // expected-case count (e.g. a cancelled session's cases) until a manual refresh. Subscribing directly:
  //   • onChanged() → parent refetches the day → fresh `outputs`/`transfers` props flow in (Select step)
  //   • re-fetch confirmData, guarded by confirmReqRef so a late resolve can't clobber newer state (Confirm)
  // `production-session-created` covers the live-ADD case (a manager adding a feeder), same as the parent.
  // CodeAnt review: the parent passes `onChanged` as an inline arrow (new identity every render), so
  // depending on it directly would tear down + re-register both WS listeners on every parent re-render —
  // and a WS-driven parent refresh re-renders on each event, leaving a tiny off→on gap where a burst of
  // session updates could be missed. Instead we subscribe ONCE per open modal (deps [isOpen, date]) and
  // read the volatile `onChanged`/`step` through refs inside the handler, so neither a new `onChanged`
  // identity nor a `step` change re-subscribes. (`date` is a genuine subscription key — it changes the
  // day the Confirm fetch targets — so it stays a real dep and is read directly.)
  // Copilot review: update these refs DURING render (not in a post-commit effect) so the "latest value"
  // guarantee is immediate — a WS event firing between a re-render and a passive effect would otherwise
  // read the previous render's values. Writing a ref during render is safe here (no external side effect).
  const onChangedRef = useRef(onChanged);
  const stepRef = useRef(step);
  onChangedRef.current = onChanged;
  stepRef.current = step;
  useEffect(() => {
    if (!isOpen) return;
    const handleSessionWs = () => {
      onChangedRef.current?.();
      if (stepRef.current === 'confirm') {
        // Manage confirmLoading exactly like the main Confirm fetch: bumping confirmReqRef here would
        // otherwise strand an in-flight main fetch (its .finally sees a stale reqId and skips
        // setConfirmLoading(false)), leaving the modal stuck on a spinner. This fetch owns the reset.
        setConfirmLoading(true);
        const reqId = ++confirmReqRef.current;
        dailyTransferOutService.getConfirmSummary(date)
          .then((next) => { if (reqId === confirmReqRef.current) setConfirmData(next); })
          .catch(() => { /* keep prior data on a transient error */ })
          .finally(() => { if (reqId === confirmReqRef.current) setConfirmLoading(false); });
      }
    };
    wsService.connect();
    wsService.on('production-session-updated', handleSessionWs);
    wsService.on('production-session-created', handleSessionWs);
    return () => {
      wsService.off('production-session-updated', handleSessionWs);
      wsService.off('production-session-created', handleSessionWs);
    };
  }, [isOpen, date]);

  // WCO-2728 (Copilot follow-up): the Scan-step header shows `scannedCount / selected.expected`, but for an
  // OUTPUT row `selected.expected` is snapshotted at row-selection and never re-synced. When a session
  // cancel/add elsewhere refetches the parent, the `outputs` prop's target updates but the Scan denominator
  // would stay stale (e.g. still `/9` after a feeder was cancelled). Re-sync `selected.expected` from the
  // fresh `outputs` prop whenever it changes. (Transfers are intentionally excluded — their `expected` is
  // scan-derived `N/N`, set on each scan above; they have no session-driven target to re-sync.)
  useEffect(() => {
    if (!selected || selected.type !== 'output') return;
    const o = outputs.find((x) => x.output_number === selected.number);
    // CodeAnt review: if a live refresh removed the selected output (its feeding sessions were all
    // cancelled → the output dropped or went non-'active') while the operator is on the Scan step,
    // don't strand them scanning into a phantom output. Clear the selection and send them back to Select.
    // Only redirect from the scan step — `selected` also persists through Confirm, which must not bounce.
    if (!o || o.status !== 'active') {
      if (step === 'scan') {
        setSelected(null);
        setStep('select');
      }
      return;
    }
    const next = o.suggested_target ?? o.expected_cases;
    if (next != null && next !== selected.expected) {
      setSelected((prev) => (prev && prev.type === 'output' && prev.number === selected.number ? { ...prev, expected: next } : prev));
    }
  }, [outputs, selected, step]);

  // Keep the badge input focused while on the badge step — including right after a wrong badge re-enables
  // the input — so the supervisor can immediately re-scan without tapping the field again.
  useEffect(() => {
    if (isOpen && step === 'badge' && !badgeLoading) badgeInputRef.current?.focus();
  }, [isOpen, step, badgeLoading]);

  // Reset to badge step each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setStep('badge');
      setAutoAdvancedToConfirm(false); // WCO-2680: don't leak an auto-advanced flag across opens
      setEmployee(null);
      setBadgeEntry('');
      setSelected(null);
      setScans([]);
      setScannedCount(0);
    }
  }, [isOpen]);

  // Step 1 — badge. ONLY a registered supervisor is allowed (authService.verifySupervisorBadge);
  // any other employee badge is rejected, same gate as the supervisor-override flow.
  const validateBadge = async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const employeeNumber = isBadgeCode(trimmed) ? extractEmployeeNumber(trimmed) : trimmed;
    setBadgeLoading(true);
    try {
      const result = await authService.verifySupervisorBadge(employeeNumber);
      if (!result.valid || !result.userId) {
        toast.error(t('supervisorOverride.badgeInvalid'));
        setBadgeEntry('');
        return;
      }
      setEmployee({ employee_number: result.employeeNumber || employeeNumber, display_name: result.fullName || employeeNumber });
      // WCO-2680: if there's nothing left to scan (all outputs/transfers already sent), skip the Select
      // step and land on the Confirm summary, which shows the day's rows as Sent/complete.
      setAutoAdvancedToConfirm(nothingLeftToScan);
      setStep(nothingLeftToScan ? 'confirm' : 'select');
    } catch {
      toast.error(t('supervisorOverride.badgeInvalid'));
    } finally {
      setBadgeLoading(false);
    }
  };

  // Load scans for the selected row (routes by type).
  const refreshSelected = useCallback(async (row: SelectedRow | null) => {
    if (!row) return;
    try {
      if (row.type === 'output') {
        const r = await productionOutputScanService.getOutputScans(row.number);
        setScans(r.scans.map((s) => ({ id: s.id, label: s.item_number || s.barcode, weight: s.actual_weight, source: s.source, barcode: s.barcode, name: s.item_english_name, sessionNumber: s.session_number })));
        // Only FINISHED-product scans count toward expected_cases; FP-NI / by-products are extras.
        const finished = r.scans.filter((s) => s.source === 'finished').length;
        setScannedCount(finished);
        setExtraCount(r.scans.length - finished);
      } else {
        // Raw-material transfer: resolved scans (item number + English name + weight), like finished products.
        const r = await dailyTransferOutService.getRawMaterialTransferScans(row.number);
        setScans(r.scans.map((s: any) => ({ id: String(s.id), label: s.item_number || s.barcode, weight: s.actual_weight ?? null, barcode: s.barcode, name: s.item_english_name })));
        setScannedCount(r.scans.length);
        setExtraCount(0);
        // Transfers are scan-only: the denominator tracks the scanned count so the counter reads N/N
        // (0/0 -> 1/1), not the stale total captured when the (empty) transfer was selected.
        setSelected((prev) => (prev && prev.type === 'transfer' && prev.number === row.number) ? { ...prev, expected: r.scans.length } : prev);
      }
    } catch { /* ignore */ }
  }, []);

  // Step 2 — pick a row → load its scans → step 3
  const selectRow = async (row: SelectedRow) => {
    setSelected(row);
    setScans([]);
    setScannedCount(0);
    setExtraCount(0);
    setExpandedGroups(new Set()); // WCO-2413: each output starts with groups collapsed
    setShowManualDamaged(false);
    setManualCases([]);
    await refreshSelected(row);
    if (row.type === 'transfer') await loadManualCases(row.number); // WCO-2545: available damaged cases
    setStep('scan');
  };

  // Step 3 — scan a case into the selected row (routes by type)
  const handleScan = useCallback(async (barcode: string) => {
    if (!selected || !employee) return;
    try {
      if (selected.type === 'output') {
        await productionOutputScanService.scanToOutput(selected.number, barcode, employee.employee_number);
      } else {
        // Raw-material transfer. Try to resolve an intact supplier box the SAME way transfer-in receiving does
        // (parseGS1128Barcode → lookupGtin) and pass {item_number, weight}. We ALWAYS post the raw barcode too:
        // our own remainder/rejected labels are ALSO GS1 (GTIN+weight), so the backend tries the label match
        // FIRST and only uses the resolved item/weight for a genuine intact box. If lookup finds nothing we
        // still post (backend resolves it as a label, or errors if truly unknown).
        const parsed = parseGS1128Barcode(barcode);
        let resolved: { itemNumber: string; weight: number } | undefined;
        if (parsed?.gtin && parsed.normalizedWeightLb) {
          const looked = await inventoryService.lookupGtin(parsed.gtin).catch(() => null);
          if (looked?.item_number) resolved = { itemNumber: looked.item_number, weight: parsed.normalizedWeightLb };
        }
        await dailyTransferOutService.scanRawMaterialToTransfer(selected.number, barcode, employee.employee_number, resolved);
      }
      toast.success(t('shipOutScan.boxScanned'));
      await refreshSelected(selected);
      onChanged?.();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || err?.response?.data?.message;
      // WCO-2465: prefer a localized message for known error codes; fall back to the backend's
      // English `msg`, then a generic default. Codes carry optional `data` for interpolation.
      const code: string | undefined = err?.response?.data?.code;
      const data = err?.response?.data?.data ?? {};
      const codeMsg = code ? scanErrorMessage(t, code, data) : undefined;
      if (codeMsg) toast.error(codeMsg);
      else if (status === 409) toast.error(msg || t('shipOutScan.duplicateBox'));
      else toast.error(msg || t('shipOutScan.scanFailed'));
    }
  }, [selected, employee, t, refreshSelected, onChanged]);

  const { manualEntry, setManualEntry } = useBarcodeScanner({
    onScan: handleScan,
    enabled: isOpen && step === 'scan',
    minLength: 3,
  });

  // WCO-2545: manual "Add damaged case" — a damaged/unreadable intact label can't be scanned. The cases
  // were WEIGHED at waste tracking, so we list the still-available MANUAL cases (item + recorded weight)
  // and let the supervisor click one to add it at that weight — no typing. Only for raw-material transfers.
  const [showManualDamaged, setShowManualDamaged] = useState(false);
  const [manualCases, setManualCases] = useState<Array<{ dto_item_id: string; item_number: string; item_english_name?: string; weight: number }>>([]);
  const [addingManualKey, setAddingManualKey] = useState<string | null>(null);
  // WCO-2599: track the latest manual-cases request so an out-of-order response (switching transfers
  // under network lag) can't overwrite the current selection's list with a stale transfer's cases.
  const manualCasesReqRef = useRef<string | null>(null);
  const loadManualCases = useCallback(async (transferNumber: string) => {
    manualCasesReqRef.current = transferNumber;
    try {
      const r = await dailyTransferOutService.getManualCasesForTransfer(transferNumber);
      if (manualCasesReqRef.current !== transferNumber) return; // a newer load superseded this one
      setManualCases(r.manualCases || []);
    } catch {
      if (manualCasesReqRef.current === transferNumber) setManualCases([]);
    }
  }, []);
  const addManualCase = async (c: { dto_item_id: string; item_number: string; weight: number }, idx: number) => {
    if (!selected || selected.type !== 'transfer' || !employee) return;
    setAddingManualKey(`${c.dto_item_id}-${idx}`);
    try {
      await dailyTransferOutService.addManualRawMaterialToTransfer(selected.number, c.item_number, c.weight, employee.employee_number, c.dto_item_id);
      toast.success(t('shipOutScan.manualDamagedAdded'));
      await Promise.all([refreshSelected(selected), loadManualCases(selected.number)]);
      onChanged?.();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(msg || t('shipOutScan.scanFailed'));
    } finally {
      setAddingManualKey(null);
    }
  };

  const handleUndo = async (scanId: string) => {
    if (!selected) return;
    try {
      if (selected.type === 'output') await productionOutputScanService.undoOutputScan(selected.number, scanId);
      else await dailyTransferOutService.undoRawMaterialTransferScan(selected.number, scanId);
      await refreshSelected(selected);
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to undo');
    }
  };

  // Confirm & Send. WCO-2433: pass a container-number subset to send only those (each gated on its own
  // completeness); omit to send ALL currently-complete containers ("Send all ready") — the backend 409s on
  // any short container. On success the sent containers drop out; we REFETCH the summary and keep the modal
  // open so remaining/incomplete containers stay for continued scanning, closing only when none remain.
  const sendContainers = async (containerNumbers?: string[]) => {
    if (!employee) return;
    setSending(true);
    try {
      const sentNumbers = await dailyTransferOutService.confirmDay(date, employee.employee_number, containerNumbers)
        .then((r) => (r?.confirmed ?? []).map((c: { number: string }) => c.number));
      toast.success(t('productionOutputScan.sent'));
      setSendTarget(null);
      setSending(false); // send is committed — release the button before the background reconcile

      // WCO-2641: OPTIMISTICALLY flip the just-sent containers to sent NOW so the "all sent" completed state
      // (WCO-2680 banner + Done) renders the instant the send returns — instead of blocking the UI on a
      // post-send confirm-summary refetch (~500ms) + the parent dto refresh. We know exactly what shipped
      // (the backend echoes `confirmed`). Then reconcile from the server in the BACKGROUND.
      const shipped = new Set(sentNumbers.length ? sentNumbers : (containerNumbers ?? []));
      setConfirmData((prev) => prev && {
        ...prev,
        containers: prev.containers.map((c) =>
          shipped.has(c.number) ? { ...c, sent: true, complete: true, ship_status: 'pending' as const } : c),
      });
      // Background reconcile (non-blocking): refresh the parent page + re-sync the review from the server.
      // Guard with a monotonic token so an older fetch resolving late can't overwrite a newer send's state.
      onChanged?.();
      const reqId = ++confirmReqRef.current;
      dailyTransferOutService.getConfirmSummary(date)
        .then((next) => { if (next && reqId === confirmReqRef.current) setConfirmData(next); })
        .catch(() => { /* optimistic state stands until the next open/refresh */ });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || t('productionOutputScan.incompleteBlocked'));
      setSending(false);
    }
  };

  // Delete a NOT-YET-SENT container (draft transfer / ad-hoc output). Its number is freed for reuse.
  // Opens the styled confirmation popup; the actual delete runs in confirmDelete.
  const handleDelete = (type: RowType, number: string) => setDeleteTarget({ type, number });

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'transfer') await dailyTransferOutService.deleteTransfer(deleteTarget.number);
      else await dailyTransferOutService.deleteAdHocOutput(deleteTarget.number);
      toast.success(t('productionOutputScan.deleted'));
      setDeleteTarget(null);
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  const steps: Array<{ key: Step; label: string; icon: React.ReactNode }> = [
    { key: 'badge', label: t('shipOutScan.scanBadge'), icon: <User className="w-4 h-4" /> },
    { key: 'select', label: t('productionOutputScan.selectOutput'), icon: <Package className="w-4 h-4" /> },
    { key: 'scan', label: t('productionOutputScan.scanCases'), icon: <Check className="w-4 h-4" /> },
    { key: 'confirm', label: t('productionOutputScan.confirmStep'), icon: <ListChecks className="w-4 h-4" /> },
  ];
  const stepIndex = steps.findIndex((s) => s.key === step);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full mx-4 max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('productionOutputScan.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-start justify-center gap-2 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          {steps.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  i < stepIndex ? 'bg-green-500 text-white'
                    : i === stepIndex ? 'bg-primary-600 text-white ring-2 ring-primary-300'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400'}`}>
                  {i < stepIndex ? <Check className="w-4 h-4" /> : s.icon}
                </div>
                <span className={`text-[11px] ${i === stepIndex ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-400'}`}>{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`h-0.5 w-10 mt-4 ${i < stepIndex ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 min-h-0">
          {/* STEP 1 — badge */}
          {step === 'badge' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('shipOutScan.scanBadge')}</p>
              <input
                ref={badgeInputRef}
                autoFocus
                inputMode="none"
                onTouchStart={(e) => (e.currentTarget.inputMode = 'text')}
                value={badgeEntry}
                onChange={(e) => setBadgeEntry(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') validateBadge(badgeEntry); }}
                disabled={badgeLoading}
                placeholder={t('shipOutScan.enterBadge')}
                className="w-full px-4 py-3 text-sm rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

          {/* STEP 2 — select output / transfer */}
          {step === 'select' && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                {employee && <><User className="w-3 h-3 inline mr-1" />{employee.display_name} </>}
                {t('productionOutputScan.selectPrompt')}
              </p>
              {activeOutputs.length === 0 && transfers.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">{t('productionOutputScan.noRows')}</p>
              )}
              {activeOutputs.map((o) => {
                // WCO-2433: a SENT output is display-only — greyed, not clickable, no delete, "Sent" badge.
                const sent = isSentOutput(o);
                return (
                <div
                  key={`o-${o.output_number}`}
                  className={`w-full flex items-center rounded-lg border border-gray-200 dark:border-gray-700 ${sent ? 'opacity-60 bg-gray-50/60 dark:bg-gray-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                >
                  <button
                    onClick={() => { if (!sent) selectRow({ type: 'output', number: o.output_number, destination: o.destination_warehouse, expected: o.suggested_target ?? o.expected_cases }); }}
                    disabled={sent}
                    className={`flex-1 min-w-0 flex items-center gap-1.5 px-3 sm:px-4 py-3 text-left ${sent ? 'cursor-default' : ''}`}
                  >
                    <Package className="w-3 h-3 text-green-600 shrink-0" />
                    <span className="w-10 sm:w-16 shrink-0 text-[9px] sm:text-xs font-medium text-green-700 dark:text-green-400 uppercase">{t('productionOutputScan.outputRow')}</span>
                    <span className="w-12 sm:w-20 shrink-0 ml-1 font-mono text-[11px] sm:text-sm text-gray-900 dark:text-gray-100">{o.output_number}</span>
                    <span className="flex-1 min-w-0 truncate text-[11px] sm:text-sm text-gray-500 dark:text-gray-400">{locName(o.destination_warehouse)}</span>
                    {/* WCO-2414: the target denominator with a hover breakdown (Plan / +/- / Target)
                        explaining how it was derived. Rendered via a portal (HoverTooltip) so it's never
                        clipped by the scrollable modal body, regardless of the row's position. */}
                    <HoverTooltip
                      className="shrink-0 whitespace-nowrap text-right text-[10px] sm:text-xs text-gray-500 dark:text-gray-400"
                      panelClassName="z-[70] w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-2.5 text-left"
                      content={o.session_breakdown && o.session_breakdown.length > 0 ? (
                        <>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">{t('productionOutputScan.targetBreakdown')}</div>
                          {/* Column header */}
                          <div className="flex items-center gap-2 pb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                            <span className="flex-1" />
                            <span className="w-9 text-right">{t('productionOutputScan.colPlan')}</span>
                            <span className="w-9 text-right">+/−</span>
                            <span className="w-9 text-right">{t('productionOutputScan.colTarget')}</span>
                          </div>
                          {o.session_breakdown.map((b) => (
                            <div key={b.session_number} className="flex items-start gap-2 py-0.5 text-[11px]">
                              {/* Item on top, session number on its own line so it never truncates. */}
                              <span className="flex-1 min-w-0">
                                <span className="block truncate text-gray-700 dark:text-gray-300">{b.item_number || b.session_number}</span>
                                {b.item_number && <span className="block font-mono text-[10px] text-gray-400">{b.session_number}</span>}
                              </span>
                              <span className="w-9 text-right tabular-nums text-gray-500 dark:text-gray-400">{b.planned}</span>
                              <span className={`w-9 text-right tabular-nums ${b.delta > 0 ? 'text-amber-600 dark:text-amber-400' : b.delta < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-300 dark:text-gray-600'}`}>
                                {b.delta > 0 ? `+${b.delta}` : b.delta < 0 ? String(b.delta) : '—'}
                              </span>
                              <span className="w-9 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">{b.target}</span>
                            </div>
                          ))}
                          <div className="flex items-center gap-2 pt-1 mt-1 border-t border-gray-200 dark:border-gray-700 text-[11px] font-semibold text-gray-900 dark:text-white">
                            <span className="flex-1">{t('productionOutputScan.targetTotal')}</span>
                            <span className="w-9" />
                            <span className="w-9" />
                            <span className="w-9 text-right tabular-nums">{o.suggested_target ?? o.expected_cases}</span>
                          </div>
                        </>
                      ) : null}
                    >
                      <span className={o.session_breakdown?.length ? 'underline decoration-dotted decoration-gray-400 underline-offset-2 cursor-help' : ''}>
                        {o.actual_cases}/{o.suggested_target ?? o.expected_cases} {t('productionOutputScan.cases')}
                      </span>
                      {o.extra_cases ? <span className="text-amber-600 dark:text-amber-400"> +{o.extra_cases}</span> : null}
                    </HoverTooltip>
                  </button>
                  {/* WCO-2433: a sent output shows a green "Sent" badge instead of the delete action. */}
                  {sent ? (
                    <span
                      className="inline-flex items-center gap-1 mr-3 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 rounded-full shrink-0"
                      title={t('productionOutputScan.sentHint')}
                    >
                      <CheckCircle2 className="w-3 h-3" />{t('productionOutputScan.sentBadge')}
                    </span>
                  ) : o.is_ad_hoc && (
                    <button onClick={() => handleDelete('output', o.output_number)} className="px-2.5 py-3 text-gray-400 hover:text-red-600 shrink-0" title={t('productionOutputScan.delete')}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                );
              })}
              {transfers.map((tr) => {
                // WCO-2650: a SENT transfer (status flipped draft → pending/... on send) is display-only —
                // greyed, not clickable, no delete, "Sent" badge — matching how WCO-2433 renders sent outputs.
                const sent = (tr.status ?? 'draft') !== 'draft';
                return (
                <div
                  key={`t-${tr.transfer_number}`}
                  className={`w-full flex items-center rounded-lg border border-gray-200 dark:border-gray-700 ${sent ? 'opacity-60' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                >
                  <button
                    onClick={() => selectRow({ type: 'transfer', number: tr.transfer_number, destination: tr.destination_warehouse, expected: tr.total_items })}
                    disabled={sent}
                    className="flex-1 min-w-0 flex items-center gap-1.5 px-3 sm:px-4 py-3 text-left disabled:cursor-default"
                  >
                    <Truck className="w-3 h-3 text-blue-600 shrink-0" />
                    <span className="w-10 sm:w-16 shrink-0 text-[9px] sm:text-xs font-medium text-blue-700 dark:text-blue-400 uppercase">{t('productionOutputScan.transferRow')}</span>
                    <span className="w-12 sm:w-20 shrink-0 ml-1 font-mono text-[11px] sm:text-sm text-gray-900 dark:text-gray-100">{tr.transfer_number}</span>
                    <span className="flex-1 min-w-0 truncate text-[11px] sm:text-sm text-gray-500 dark:text-gray-400">{locName(tr.destination_warehouse)}</span>
                    <span className="shrink-0 whitespace-nowrap text-right text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">{tr.scanned_count}/{tr.total_items} {t('productionOutputScan.cases')}</span>
                  </button>
                  {sent ? (
                    <span className="px-2.5 py-1 mr-2 text-xs font-medium text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400 rounded-md shrink-0">{t('productionOutputScan.sent')}</span>
                  ) : (
                    <button onClick={() => handleDelete('transfer', tr.transfer_number)} className="px-2.5 py-3 text-gray-400 hover:text-red-600 shrink-0" title={t('productionOutputScan.delete')}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
              })}
            </div>
          )}

          {/* STEP 3 — scan cases */}
          {step === 'scan' && selected && (
            <div className="flex flex-col h-full min-h-0 space-y-3">
              <div className="flex items-center justify-between shrink-0">
                <button onClick={() => { setStep('select'); setSelected(null); }} className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900">
                  <ArrowLeft className="w-4 h-4" />{t('productionOutputScan.switch')}
                </button>
                <span className="text-sm">
                  <span className="text-xs uppercase mr-1 text-gray-400">{selected.type === 'output' ? t('productionOutputScan.outputRow') : t('productionOutputScan.transferRow')}</span>
                  <span className="font-mono text-gray-900 dark:text-gray-100">{selected.number}</span>
                  <span className="ml-2 font-semibold text-gray-900 dark:text-white">{scannedCount}/{selected.expected}</span>
                  {extraCount > 0 && (
                    <span className="ml-1 text-xs text-amber-600 dark:text-amber-400" title={t('productionOutputScan.extrasHint')}>+{extraCount}</span>
                  )}
                </span>
              </div>

              <input
                autoFocus
                inputMode="none"
                onTouchStart={(e) => (e.currentTarget.inputMode = 'text')}
                value={manualEntry}
                onChange={(e) => setManualEntry(e.target.value)}
                placeholder={t('productionOutputScan.scanPrompt')}
                className="w-full px-4 py-3 text-sm rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 shrink-0"
              />

              {/* WCO-2545: damaged/unreadable cases (raw-material transfers only) can't be scanned — but they
                  were WEIGHED at waste tracking, so list the available ones and click to add at that weight. */}
              {selected.type === 'transfer' && manualCases.length > 0 && (
                <div className="shrink-0">
                  {!showManualDamaged ? (
                    <button
                      type="button"
                      onClick={() => setShowManualDamaged(true)}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                    >
                      <Plus className="w-4 h-4" />{t('shipOutScan.addDamagedCase')} ({manualCases.length})
                    </button>
                  ) : (
                    <div className="p-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10 space-y-2">
                      <div className="flex items-center justify-between px-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{t('shipOutScan.damagedCasesAvailable')}</span>
                        <button type="button" onClick={() => setShowManualDamaged(false)} className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">{t('common.cancel')}</button>
                      </div>
                      {manualCases.map((c, idx) => {
                        const key = `${c.dto_item_id}-${idx}`;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => addManualCase(c, idx)}
                            disabled={addingManualKey !== null}
                            className="w-full flex items-center justify-between gap-2 px-3 py-3 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50"
                          >
                            <span className="truncate font-medium text-gray-900 dark:text-white">
                              {c.item_english_name ? `${c.item_number} — ${c.item_english_name}` : c.item_number}
                            </span>
                            <span className="tabular-nums text-gray-700 dark:text-gray-200 shrink-0">{c.weight.toFixed(2)} lb</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {scans.length === 0 && selected.type !== 'output' && <p className="text-sm text-gray-400 text-center py-4">{t('productionOutputScan.noScans')}</p>}

                {/* WCO-2413: OUTPUT rows show a grouped, self-reconcilable view (one group per feeding
                    session→product with its own scanned/target counter + surplus/deficit flag). Transfers
                    keep the simple flat list (no producing session). */}
                {selected.type === 'output' ? (
                  <div className="space-y-3">
                    {scanGroups.groups.map((g) => {
                      const scannedInGroup = g.scans.length;
                      const groupComplete = g.target > 0 && scannedInGroup >= g.target;
                      const isExpanded = expandedGroups.has(g.session_number);
                      return (
                        <div key={g.session_number} className="rounded-lg border border-gray-200 dark:border-gray-700">
                          {/* Group header (click to expand/collapse the box list). Collapsed by default —
                              the scanned/target counter is the at-a-glance reconciliation aid. */}
                          <button
                            type="button"
                            onClick={() => toggleGroup(g.session_number)}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/40 ${isExpanded ? 'rounded-t-lg' : 'rounded-lg'} hover:bg-gray-100 dark:hover:bg-gray-900/60 text-left`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {g.item_english_name || g.item_number || g.session_number}
                                  {g.item_number && g.item_english_name && <span className="ml-1.5 text-[10px] font-mono text-gray-400">{g.item_number}</span>}
                                </div>
                                <div className="text-[11px] font-mono text-gray-400 dark:text-gray-500">{g.session_number}</div>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <span className={`text-sm font-semibold ${groupComplete ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                                {scannedInGroup}/{g.target}
                              </span>
                              {g.delta !== 0 && (
                                <div className={`text-[10px] ${g.delta > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                                  {g.delta > 0
                                    ? t('productionOutputScan.plannedSurplus').replace('{{planned}}', String(g.planned)).replace('{{delta}}', String(g.delta))
                                    : t('productionOutputScan.plannedDeficit').replace('{{planned}}', String(g.planned)).replace('{{delta}}', String(-g.delta))}
                                </div>
                              )}
                            </div>
                          </button>
                          {/* Scans within this group — hidden until the group is expanded. */}
                          {isExpanded && (
                            <div className="divide-y divide-gray-100 dark:divide-gray-700/60 px-3">
                              {g.scans.length === 0 && <p className="text-xs text-gray-400 py-2">{t('productionOutputScan.noScans')}</p>}
                              {g.scans.map((s) => <ScanLine key={s.id} s={s} t={t} onUndo={handleUndo} />)}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Extras group: FP-NI / by-product scans (no feeding session) — not gating. */}
                    {scanGroups.extras.length > 0 && (
                      <div className="rounded-lg border border-amber-200 dark:border-amber-800/60">
                        <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-t-lg text-sm font-medium text-amber-700 dark:text-amber-400">
                          {t('productionOutputScan.extrasGroup')} ({scanGroups.extras.length})
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-700/60 px-3">
                          {scanGroups.extras.map((s) => <ScanLine key={s.id} s={s} t={t} onUndo={handleUndo} />)}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // WCO-2545: transfer scans in collapsible per-item groups (mirrors the output view),
                  // collapsed by default so the manual "Add damaged case" list stays prominent.
                  <div className="space-y-2">
                    {transferScanGroups.map((g) => {
                      const isExpanded = expandedGroups.has(g.item_number);
                      return (
                        <div key={g.item_number} className="rounded-lg border border-gray-200 dark:border-gray-700">
                          <button
                            type="button"
                            onClick={() => toggleGroup(g.item_number)}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-3 bg-gray-50 dark:bg-gray-900/40 ${isExpanded ? 'rounded-t-lg' : 'rounded-lg'} hover:bg-gray-100 dark:hover:bg-gray-900/60 text-left`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {g.item_english_name || g.item_number}
                                </div>
                                <div className="text-[11px] font-mono text-gray-400 dark:text-gray-500">{g.item_number}</div>
                              </div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white shrink-0">{g.scans.length}</span>
                          </button>
                          {isExpanded && (
                            <div className="divide-y divide-gray-100 dark:divide-gray-700/60 px-3">
                              {g.scans.map((s, idx) => (
                                <div key={s.id} className="flex items-start justify-between py-2 gap-3">
                                  <div className="flex gap-2 min-w-0">
                                    <span className="text-gray-400 text-sm mt-0.5">{g.scans.length - idx}</span>
                                    <div className="min-w-0">
                                      {s.barcode && <div className="text-[11px] font-mono text-gray-500 dark:text-gray-400 truncate">{s.barcode}</div>}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 shrink-0">
                                    {s.weight != null && <span className="text-xs text-gray-500">{Number(s.weight).toFixed(2)} lb</span>}
                                    <button onClick={() => handleUndo(s.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 4 — confirm (day-wide review of every not-yet-sent container) */}
          {step === 'confirm' && (
            <div className="space-y-3">
              {employee && (
                <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 mb-1"><User className="w-3 h-3 inline mr-1" />{employee.display_name}</p>
              )}
              {/* When nothing is left to send (auto-advanced here, OR everything just got sent via "Send all
                  ready"), lead with an explicit "all sent" banner so the completed state reads as
                  intentional, not as a review with a dead Send button. */}
              {showAllSentState && !confirmLoading ? (
                <div className="flex items-center gap-2 rounded-lg border border-green-300 dark:border-green-700/60 bg-green-50 dark:bg-green-900/20 px-3 py-2.5 text-sm text-green-700 dark:text-green-300">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>{t('productionOutputScan.allSentComplete')}</span>
                </div>
              ) : (
                <p className="hidden sm:block text-sm text-gray-600 dark:text-gray-300">{t('productionOutputScan.confirmPrompt')}</p>
              )}
              {confirmLoading && <p className="text-sm text-gray-400 text-center py-6">{t('common.processing')}</p>}
              {!confirmLoading && confirmData && confirmData.containers.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">{t('productionOutputScan.noRows')}</p>
              )}
              {!confirmLoading && confirmData && confirmData.containers.map((c) => (
                <div key={`${c.type}-${c.number}`} className={`rounded-lg border p-3 ${c.sent ? 'border-gray-200 dark:border-gray-700 opacity-60 bg-gray-50/60 dark:bg-gray-900/30' : c.complete ? 'border-gray-200 dark:border-gray-700' : 'border-red-300 dark:border-red-700/60'}`}>
                  <div className="flex items-center justify-between gap-x-2 gap-y-1 flex-wrap mb-2">
                    <span className="flex items-center gap-2 min-w-0">
                      {c.type === 'output' ? <Package className="w-4 h-4 text-green-600 shrink-0" /> : <Truck className="w-4 h-4 text-blue-600 shrink-0" />}
                      <span className="font-mono text-sm text-gray-900 dark:text-gray-100 shrink-0">{c.number}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 truncate">{locName(c.destination)}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0 ml-auto">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{c.scanned}/{c.expected}{c.extra ? <span className="text-amber-600 dark:text-amber-400"> +{c.extra}</span> : null}</span>
                      {/* WCO-2433: a SENT container is display-only — show a "Sent" badge, no status flag, no Send. */}
                      {c.sent
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 rounded-full" title={t('productionOutputScan.sentHint')}><CheckCircle2 className="w-3.5 h-3.5" />{t('productionOutputScan.sentBadge')}</span>
                        : c.complete
                        ? <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircle2 className="w-3.5 h-3.5" />{t('productionOutputScan.complete')}</span>
                        : <span className="text-xs font-medium text-red-600 dark:text-red-400">{t('productionOutputScan.incomplete')}</span>}
                      {/* WCO-2433: send THIS complete container on its own, independently of the rest of the day. */}
                      {!c.sent && c.complete && (
                        <button
                          type="button"
                          onClick={() => setSendTarget(c.number)}
                          disabled={sending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />{t('productionOutputScan.send')}
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                    {c.items.length === 0 && <p className="text-xs text-gray-400 py-1">{t('productionOutputScan.noScans')}</p>}
                    {/* WCO-2412: aggregate scanned boxes by item — one row per item_number showing
                        qty (case count) and the SUMMED weight (cases can be catch-weight, so weight
                        is summed per case, never qty×unit). Keeps the FP-NI / by-product tag. */}
                    {aggregateConfirmItems(c.items).map((it) => {
                      const rowKey = `${c.type}-${c.number}|${it.key}`;
                      const isOpen = expandedConfirmItems.has(rowKey);
                      return (
                        <div key={it.key}>
                          {/* Aggregated item row — click to expand the individual boxes. */}
                          <button
                            type="button"
                            onClick={() => toggleConfirmItem(rowKey)}
                            className="w-full flex items-center justify-between py-1.5 text-sm gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded"
                          >
                            <span className="flex items-center gap-1.5 min-w-0 text-xs sm:text-sm text-gray-800 dark:text-gray-200 truncate">
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                              <span className="truncate">
                                {it.item_number}{it.item_english_name ? ` - ${it.item_english_name}` : ''}
                                {it.source && it.source !== 'finished' && (
                                  <span className={`ml-1.5 text-[10px] uppercase tracking-wide ${it.source === 'fpni' ? FP_NI_TAG_CLASSES : 'text-amber-600 dark:text-amber-400'}`}>
                                    {it.source === 'fpni' ? t('productionOutputScan.fpniTag') : t('productionOutputScan.byproductTag')}
                                  </span>
                                )}
                              </span>
                            </span>
                            <span className="flex items-center gap-2 shrink-0 text-xs text-gray-500">
                              <span className="text-gray-700 dark:text-gray-300 font-medium">×{it.qty}</span>
                              {it.total_weight > 0 && <span>{it.total_weight.toFixed(2)} lb</span>}
                            </span>
                          </button>
                          {/* Individual boxes making up this item's aggregate (barcode + weight). */}
                          {isOpen && (
                            <div className="pl-5 pb-1 space-y-0.5">
                              {it.boxes.map((b) => (
                                <div key={b.id} className="flex items-center justify-between gap-3 text-[11px] text-gray-500 dark:text-gray-400">
                                  <span className="font-mono truncate">{b.barcode}</span>
                                  {b.actual_weight != null && <span className="shrink-0">{Number(b.actual_weight).toFixed(2)} lb</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer — navigate to the day-wide Confirm review; the actual send is wired in a later phase. */}
        <div className="flex justify-between items-center px-5 py-4 border-t border-gray-200 dark:border-gray-700 min-h-[68px]">
          {step === 'confirm' && !showAllSentState
            ? <button onClick={() => setStep('select')} className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900"><ArrowLeft className="w-4 h-4" />{t('productionOutputScan.backToScan')}</button>
            : <span />}
          {/* "All sent" state (auto-advanced OR everything just sent) — the only action is to close. */}
          {step === 'confirm' && showAllSentState && (
            <button onClick={onClose} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg bg-primary-600 hover:bg-primary-700 transition-colors">
              <CheckCircle2 className="w-4 h-4" />{t('common.done')}
            </button>
          )}
          {/* WCO-2680: from the Select step, offer a direct "Next → Review" when at least one container is
              already complete + unsent — so the supervisor doesn't have to re-open an output to reach
              Confirm. Only shown when there's something eligible to send. */}
          {step === 'select' && anySendable && (
            <button onClick={() => { setAutoAdvancedToConfirm(false); setStep('confirm'); }} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg bg-primary-600 hover:bg-primary-700 transition-colors">
              <ListChecks className="w-4 h-4" />{t('productionOutputScan.reviewStep')}
            </button>
          )}
          {step === 'scan' && (activeOutputs.length > 0 || transfers.length > 0) && (
            // WCO-2680: a DELIBERATE review — clear the auto-advanced flag so a later live refresh never
            // bounces the supervisor back to Select mid-review.
            <button onClick={() => { setAutoAdvancedToConfirm(false); setStep('confirm'); }} className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${allScanned ? 'bg-primary-600 hover:bg-primary-700' : 'bg-gray-400 dark:bg-gray-600 hover:bg-gray-500 dark:hover:bg-gray-500'}`}>
              <ListChecks className="w-4 h-4" />{t('productionOutputScan.reviewConfirm')}
            </button>
          )}
          {/* WCO-2433: "Send all ready" — commit every currently-COMPLETE, UNSENT container in one go. Enabled
              when ANY unsent container is complete (no longer gated on all_complete); incomplete ones stay for
              scanning, already-sent ones are display-only and never re-sent. */}
          {step === 'confirm' && !showAllSentState && (() => {
            const anyComplete = !!confirmData?.containers.some((c) => c.complete && !c.sent);
            return (
              <button
                onClick={() => { if (!anyComplete) { toast.error(t('productionOutputScan.noneReady')); return; } setSendTarget('ALL'); }}
                disabled={sending || confirmLoading}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${anyComplete ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 dark:bg-gray-600'}`}
              >
                <CheckCircle2 className="w-4 h-4" />{t('productionOutputScan.sendAllReady')}
              </button>
            );
          })()}
        </div>

        {/* Final confirmation popup — the only point that commits to ICS production/sales/transfer tables.
            WCO-2433: sendTarget is a single container number, or 'ALL' for every currently-complete container. */}
        {sendTarget && (() => {
          const numbers = sendTarget === 'ALL'
            ? (confirmData?.containers.filter((c) => c.complete && !c.sent).map((c) => c.number) ?? [])
            : [sendTarget];
          return (
            <div className="absolute inset-0 z-[60] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/50 rounded-xl" onClick={() => !sending && setSendTarget(null)} />
              <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {sendTarget === 'ALL'
                      ? t('productionOutputScan.confirmSendAll').replace('{{count}}', String(numbers.length))
                      : `${t('productionOutputScan.confirmSend')} ${sendTarget}`}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{t('productionOutputScan.confirmPopupBody')}</p>
                  <div className="flex gap-3 w-full">
                    <button onClick={() => setSendTarget(null)} disabled={sending} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50">{t('common.cancel')}</button>
                    <button onClick={() => sendContainers(numbers)} disabled={sending} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">{sending ? t('common.processing') : t('common.confirm')}</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Styled delete confirmation (replaces the browser confirm). */}
        {deleteTarget && (
          <div className="absolute inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50 rounded-xl" onClick={() => !deleting && setDeleteTarget(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {t('productionOutputScan.delete')} <span className="font-mono">{deleteTarget.number}</span>
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{t('productionOutputScan.confirmDelete')}</p>
                <div className="flex gap-3 w-full">
                  <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50">{t('common.cancel')}</button>
                  <button onClick={confirmDelete} disabled={deleting} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">{deleting ? t('common.processing') : t('productionOutputScan.delete')}</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// WCO-2413: one scanned case line (shared by the grouped output view and the extras group).
const ScanLine: React.FC<{ s: ScanRow; t: (k: string) => string; onUndo: (id: string) => void }> = ({ s, t, onUndo }) => (
  <div className="flex items-start justify-between py-2 gap-3">
    <div className="min-w-0">
      <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
        {s.label}{s.name ? ` - ${s.name}` : ''}
        {s.source && s.source !== 'finished' && (
          <span className={`ml-1.5 text-[10px] uppercase tracking-wide ${s.source === 'fpni' ? FP_NI_TAG_CLASSES : 'text-amber-600 dark:text-amber-400'}`}>
            {s.source === 'fpni' ? t('productionOutputScan.fpniTag') : t('productionOutputScan.byproductTag')}
          </span>
        )}
      </div>
      {s.barcode && <div className="text-[10px] font-mono text-gray-400 dark:text-gray-500 truncate">{s.barcode}</div>}
    </div>
    <div className="flex items-center gap-3 shrink-0">
      {s.weight != null && <span className="text-xs text-gray-500">{Number(s.weight).toFixed(2)} lb</span>}
      <button onClick={() => onUndo(s.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
    </div>
  </div>
);

export default ProductionOutputScanModal;
